// 🔴 US-AI-0496-S03 (@UC9803) — DÙNG LẠI hằng của domain, KHÔNG khai literal thứ hai.
// Khai `= 200` lần nữa ở đây là dựng nguồn sự thật thứ hai cho một con số, và hai bản sẽ
// lệch đúng vào ngày ai đó nâng một bên: zod cho qua rồi DB ném CHECK violation — tức 500
// trên đường ghi cạnh tiền, stack trỏ vào Postgres chứ không trỏ vào dòng người đó vừa sửa.
// (Cùng họ với drift HAI whitelist `txType` mà repo đã dẫm.)
import { PAY_REPORT_NOTE_MAX_LENGTH } from "@goppy/domain";
import { z } from "zod";
// US-E19.F1-0200 (@UC9296): khoá idempotency NGHIỆP VỤ — 1 nguồn, dùng chung record-paid + báo CK.
import { businessIdempotencyKeySchema } from "./ledger-correction.js";
import { evidenceKeySchema } from "./contribution-evidence.js";
// 🔴 US-E24.F33-0447 (@UC9713) — PHẠM VI THÀNH VIÊN đã có luật ở lịch định kỳ (@UC9616, QĐ PO
// 08/08/2026). Import lại hằng biên thay vì khai bản thứ hai: hai route anh em (`/collections` một
// lần và `/collections/schedules` định kỳ) phải nhận CÙNG một danh sách trắng với CÙNG luật, nếu
// không thì client gửi đúng cho route này và sai cho route kia. Đây là quan hệ MỘT CHIỀU
// (collections → collection-schedules), không có vòng: `collection-schedules.ts` chỉ import `zod`.
import {
  COLLECTION_DESCRIPTION_MAX,
  MEMBER_IDS_DUPLICATE_MESSAGE,
  MEMBER_IDS_EMPTY_MESSAGE,
  MEMBER_IDS_TOO_MANY_MESSAGE,
  SCHEDULE_MEMBER_IDS_MAX,
} from "./collection-schedules.js";

/* ===========================================================================
 * POST /v1/funds/:id/collections — Create Ad-hoc Collection (đợt thu đột xuất one-time)
 * (US-E6.F2-0037 / @UC0039, Phase 7 Collections — @gate money,rbac).
 *
 * Cross-check index §3/§5 (PO chốt): tạo Collection (kind=adhoc, status=open, recurrence=null) +
 * sinh member_dues `pending` cho mọi member ACTIVE đồng bộ. KHÔNG schedule, KHÔNG recurrence,
 * KHÔNG ghi ledger (member_dues ở `pending` — B2). Mirror collection-schedules.ts (amount STRING
 * BigInt — B1, `.strict()` chặn over-post, refine BIGINT-overflow).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: `amountMinor` truyền dạng STRING số nguyên (chống mất chính xác JSON number >
 *    2^53). Server parse → BigInt. KHÔNG float ở bất kỳ tầng nào.
 *  - C5 `.strict()` chặn over-post (KHÔNG nhận currency/fundId/kind/status từ body — currency kế
 *    thừa fund, fundId từ :id param, kind/status do server set).
 *  - C4 RBAC OWNER/ADMIN/TREASURER (@RequireFundRoles trên FundAccessGuard); Member → 403;
 *    non-member/cross-tenant/non-UUID → 404 đồng nhất.
 *  - Validate biên: số tiền > 0 (AC3), hạn đóng không quá khứ (AC2 — kiểm tz ở service/domain).
 * ========================================================================= */

/** Loại đợt thu (khớp Collection.kind). */
export const COLLECTION_KIND = {
  PERIODIC: "periodic",
  ADHOC: "adhoc",
  SETTLEMENT: "settlement",
  /**
   * 🔴 FUND-DEBT-REPAY-PATH (QĐ PO 06/10/2026 Q3/Q4) — đợt HỆ THỐNG chứa đúng MỘT khoản: nợ của một thành viên VỚI QUỸ (`emergency_repay` ·
   * `advance_credit_clawback`) chuyển thành khoản phải đóng NGAY khi nợ phát sinh, trả bằng ba cửa thu có sẵn. Không phải đợt người mở:
   * collection-progress trả kèm `hiddenFromList: true` (client ẩn khỏi danh sách đợt + tiến độ, sheet "Nộp quỹ" vẫn ghép được); không đóng /
   * mở lại / huỷ / đổi số / đổi hạn; không vào chốt sổ, link minh bạch công khai, dòng hoạt động nhóm, thông báo "đã thu đủ"; `dueDate` NULL ⇒
   * không nhắc tự động (O/A/T nhắc tay).
   */
  FUND_DEBT: "fund_debt",
} as const;

export type CollectionKind = (typeof COLLECTION_KIND)[keyof typeof COLLECTION_KIND];

/** Trạng thái đợt thu (khớp Collection.status). */
export const COLLECTION_STATUS = {
  OPEN: "open",
  CLOSED: "closed",
} as const;

export type CollectionStatus = (typeof COLLECTION_STATUS)[keyof typeof COLLECTION_STATUS];

/**
 * Schema của trạng thái VÒNG ĐỜI đợt thu (US-E24.F2-0351-S03 · @UC9567) — một nguồn sự thật để
 * server parse fail-loud và client map. Khớp CHECK `collections_status_check` ở DB.
 */
export const collectionStatusSchema = z.enum([
  COLLECTION_STATUS.OPEN,
  COLLECTION_STATUS.CLOSED,
]);

/** Giới hạn tiêu đề đợt thu (đủ cho tiêu đề; chống over-long). */
export const ADHOC_TITLE_MAX_LENGTH = 100;

/**
 * Số chữ số tối đa cho amount (SEC MED-1): PG BIGINT max = 9223372036854775807 (19 chữ số). Chuỗi
 * số dài hơn parse BigInt OK nhưng ghi DB ném P2020 → 500. .max(19) chặn ≥20 chữ số, NHƯNG ~8.6% giá
 * trị 19 chữ số (9223372036854775808..9999999999999999999) vẫn vượt BIGINT → cần refine giá trị (dưới).
 */
export const ADHOC_AMOUNT_MAX_DIGITS = 19;

/** Trần PG signed BIGINT (9_223_372_036_854_775_807). amount > trần → 400 (chống overflow P2020→500). */
export const ADHOC_PG_BIGINT_MAX = 9223372036854775807n;

/** Thông điệp lỗi user-facing — một nguồn sự thật api ↔ mobile (khớp AC). */
export const ADHOC_AMOUNT_REQUIRED_MESSAGE = "Số tiền bắt buộc";
export const ADHOC_AMOUNT_INVALID_MESSAGE = "Số tiền phải là số nguyên (đơn vị nhỏ nhất)";
export const ADHOC_DUE_DATE_INVALID_MESSAGE = "Hạn đóng không hợp lệ (định dạng YYYY-MM-DD)";

/** Regex 'YYYY-MM-DD' (date-only). Parse → Date UTC midnight ở service. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** Số nguyên dạng string (cho amount BigInt) — không dấu chấm, không số mũ, không dấu âm. */
const INTEGER_STRING_PATTERN = /^\d+$/;

/** True nếu chuỗi 'YYYY-MM-DD' là một ngày dương lịch THẬT (chặn '2026-02-30'). */
function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split("-").map((p) => Number.parseInt(p, 10));
  if (y === undefined || m === undefined || d === undefined) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
  );
}

/**
 * Ô HẠN NỘP của đợt thu một lần: 'YYYY-MM-DD' là ngày dương lịch THẬT (trim). MỘT nguồn cho lúc tạo đợt
 * (`createAdhocCollectionRequestSchema`) và lúc nhân viên sửa hạn (`adminCollectionChangeDueDateBodySchema`,
 * US-AE4.F7-1005) — cùng regex, cùng câu báo lỗi. "Không về quá khứ" kiểm ở service theo giờ của nhóm.
 */
export const adhocDueDateFieldSchema = z
  .string()
  .trim()
  .regex(ISO_DATE_PATTERN, ADHOC_DUE_DATE_INVALID_MESSAGE)
  .refine(isRealCalendarDate, ADHOC_DUE_DATE_INVALID_MESSAGE);

/**
 * Body cho POST /v1/funds/:id/collections.
 *  - title: trim non-empty ≤100.
 *  - amountMinor: STRING số nguyên dương (B1 BigInt — server parse). ""/âm/float/overflow → 400 (AC3).
 *  - dueDate: 'YYYY-MM-DD' (ngày dương lịch THẬT). Quá khứ → 400 (AC2 — kiểm tz ở service/domain).
 *  - currency CỐ Ý VẮNG: kế thừa fund.currency (server đọc — KHÔNG nhận từ body).
 *  - `.strict()` chặn over-post (currency/fundId/kind/status).
 */
export const createAdhocCollectionRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(ADHOC_TITLE_MAX_LENGTH),
    amountMinor: z
      .string()
      .trim()
      .min(1, ADHOC_AMOUNT_REQUIRED_MESSAGE)
      .max(ADHOC_AMOUNT_MAX_DIGITS, ADHOC_AMOUNT_INVALID_MESSAGE)
      .regex(INTEGER_STRING_PATTERN, ADHOC_AMOUNT_INVALID_MESSAGE)
      // SEC MED-1 (residual): .max(19) còn lọt giá trị 19 chữ số vượt BIGINT (vd 9999999999999999999)
      // → ghi DB P2020 → 500. Refine giá trị THẬT ≤ trần BIGINT. Bọc try/catch: chuỗi không-số →
      // BigInt() ném ⇒ trả false (invalid), KHÔNG throw.
      .refine((v) => {
        try {
          return BigInt(v) <= ADHOC_PG_BIGINT_MAX;
        } catch {
          return false;
        }
      }, ADHOC_AMOUNT_INVALID_MESSAGE),
    dueDate: adhocDueDateFieldSchema,
    /**
     * 🔴 @UC9713 — PHẠM VI THÀNH VIÊN: danh sách **TRẮNG** những `group_members.id` ĐƯỢC THU.
     *
     * **VẮNG ⇒ mọi member `active`** như trước (§3.8 *"default ⇒ TẮT ⇒ 0 REGRESS"*). Hàng nghìn đợt
     * thu cũ, và đường AI (v1 `col-collection-executor.ts` đã xoá; v2 binding `col-equal`), không gửi field này ⇒ không đổi một hành vi nào.
     *
     * **`[]` ⇒ 400**, KHÔNG đọc thành "chọn tất cả": một client tính ra tập rỗng do lỗi mà server
     * hiểu thành "cả nhóm" là đi đòi tiền 40 người vì một mảng rỗng.
     * ⚠️ Khác với cột DB `collection_schedules.member_ids` (`NOT NULL DEFAULT '{}'`) nơi `[]` ĐÚNG LÀ
     * "không giới hạn" — đó là default của tầng LƯU cho lịch cũ, không phải ngữ nghĩa của REQUEST.
     *
     * **Trùng ⇒ 400, KHÔNG tự dedupe** (xem `superRefine` dưới): tự dedupe là im lặng sửa ý người
     * dùng trên chính danh sách quyết định AI PHẢI ĐÓNG TIỀN.
     *
     * 🔴 `excludeMemberIds` (danh sách ĐEN) **cố ý không tồn tại** — mirror @UC9616. Hai kiểu phạm vi
     * hành xử KHÁC NHAU với người vào nhóm sau ⇒ có cả hai là có hai luật tiền song song. `.strict()`
     * biến điều đó thành đảm bảo của KIỂU, 0 dòng validate.
     */
    memberIds: z
      .array(z.string().uuid())
      .min(1, MEMBER_IDS_EMPTY_MESSAGE)
      .max(SCHEDULE_MEMBER_IDS_MAX, MEMBER_IDS_TOO_MANY_MESSAGE)
      .optional(),
    /**
     * US-E24.F36-0450 (@UC0039) — MÔ TẢ khoản thu: "tiền này dùng vào việc gì".
     *
     * **OPTIONAL ở API, dù form mobile đánh dấu bắt buộc.** Hai tầng khác nhau và cố ý không đồng
     * bộ: `.strict()` + optional giữ cho hàng nghìn đợt thu cũ và đường AI (binding v2 `col-equal`; v1 `col-collection-executor` đã xoá)
     * (không gửi field này) chạy y nguyên — §3.8 *"default ⇒ TẮT ⇒ 0 REGRESS"*. Muốn ép người dùng
     * điền thì ép ở FORM, nơi có ngữ cảnh để nói "vì sao cần"; ép ở contract là làm hỏng mọi client
     * cũ để đổi lấy một ràng buộc mà UI vốn đã bảo đảm.
     *
     * Trần 1000 ký tự theo tiền lệ `Expense.description` (`expenses.ts:259`) — cùng vai trò, cùng
     * kiểu người dùng gõ, nên cùng trần. `.trim()` trước khi đo để "   " không thành mô tả hợp lệ.
     */
    description: z.string().trim().max(COLLECTION_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    // 🔴 So TRÙNG phải KHÔNG phân biệt hoa/thường — `z.string().uuid()` khớp case-insensitive, nên
    // `["7C9E…", "7c9e…"]` (CÙNG một người) lọt qua phép so chuỗi thô. Service chuẩn hoá lowercase
    // rồi mới kiểm sở hữu ⇒ `memberIds.length = 2` mà `groupMember.count` = 1 ⇒ **404 "không thuộc
    // nhóm"** cho một thành viên hoàn toàn có thật: vừa sai mã lỗi so với 400 đã khai, vừa bắt người
    // dùng đi tìm một thành viên lạ không hề tồn tại. (HIGH-1 của @UC9616, cùng cách vá.)
    if (
      body.memberIds &&
      new Set(body.memberIds.map((id) => id.toLowerCase())).size !== body.memberIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["memberIds"],
        message: MEMBER_IDS_DUPLICATE_MESSAGE,
      });
    }
  });

export type CreateAdhocCollectionRequest = z.infer<typeof createAdhocCollectionRequestSchema>;

/**
 * Shape response cho POST collections (201).
 *  - collection: đợt vừa tạo (id/fundId/title/kind/status/amount-as-string/currency/dueDate).
 *  - duesCreated: số member_dues sinh (mọi member ACTIVE).
 *  - amountPerMemberMinor trả dạng STRING (B1 — KHÔNG number để tránh mất chính xác). `.strict()`.
 */
export const adhocCollectionResponseSchema = z
  .object({
    collection: z
      .object({
        id: z.string().uuid(),
        fundId: z.string().uuid(),
        title: z.string(),
        kind: z.literal(COLLECTION_KIND.ADHOC),
        status: z.literal(COLLECTION_STATUS.OPEN),
        amountPerMemberMinor: z.string().regex(INTEGER_STRING_PATTERN),
        currency: z.string().length(3),
        dueDate: z.string().regex(ISO_DATE_PATTERN),
      })
      .strict(),
    duesCreated: z.number().int().nonnegative(),
  })
  .strict();

export type AdhocCollectionResponse = z.infer<typeof adhocCollectionResponseSchema>;

/* ===========================================================================
 * GET /v1/funds/:id/collections?member_id=me — View My Collection Periods to Pay
 * (US-E6.F3-0038 / @UC0057, Phase 7 Collections — @gate rbac). READ-only.
 *
 * Cross-check index §3 (PO chốt): trả các đợt thu ACTIVE của fund mà CALLER có member_due (enrolled)
 * + trạng thái đóng tiền lấy TRỰC TIẾP từ member_dues.status (KHÔNG recompute — invariant story
 * docs/USER_STORIES.md [đã xoá → docs/USECASES.md UC0038-0040/UC0057] :1542). member_id CHỈ chấp nhận 'me' (self) — server resolve groupMemberId của
 * caller từ FundAccessGuard ctx (KHÔNG tin member id client gửi — chống IDOR).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: amountMinor trả dạng STRING số nguyên (KHÔNG float/number > 2^53).
 *  - C4 RBAC + no-leak: FundAccessGuard bare (mọi member ACTIVE đọc được); non-member → 404 đồng nhất
 *    (repo 404-uniformity — chống IDOR, thoả intent "không lộ dữ liệu quỹ" của AC6).
 *  - Status từ nguồn: `status` = member_dues.status nguyên gốc. `isOverdue`/`isNearDeadline` là cờ
 *    hiển thị server-derive (KHÔNG ghi DB), KHÔNG đổi `status`.
 *  - Read-only: KHÔNG ghi ledger / KHÔNG mutate member_dues.
 * ========================================================================= */

/** Trạng thái member_due (khớp MemberDue.status — schema.prisma:493). NGUỒN sự thật render badge. */
export const MEMBER_DUE_STATUS = {
  PENDING: "pending",
  AWAITING_CONFIRMATION: "awaiting_confirmation",
  PAID: "paid",
  // US-E16.F4-0185 (@UC9241): đóng MỘT PHẦN — còn nợ = amountMinor − paidAmountMinor (partial-pay).
  PARTIALLY_PAID: "partially_paid",
  WAIVED: "waived",
  OVERDUE: "overdue",
  PAID_ON_BEHALF: "paid_on_behalf",
} as const;

export type MemberDueStatus = (typeof MEMBER_DUE_STATUS)[keyof typeof MEMBER_DUE_STATUS];

export const memberDueStatusSchema = z.enum([
  MEMBER_DUE_STATUS.PENDING,
  MEMBER_DUE_STATUS.AWAITING_CONFIRMATION,
  MEMBER_DUE_STATUS.PAID,
  MEMBER_DUE_STATUS.PARTIALLY_PAID,
  MEMBER_DUE_STATUS.WAIVED,
  MEMBER_DUE_STATUS.OVERDUE,
  MEMBER_DUE_STATUS.PAID_ON_BEHALF,
]);

/**
 * 🔴🔴 NGUỒN SỰ THẬT DUY NHẤT — status nào member CÒN TỰ BÁO CHUYỂN KHOẢN ĐƯỢC.
 *
 * ═══ VÌ SAO HẰNG NÀY TỒN TẠI (QĐ người dùng 01/09/2026) ═══
 * Trước bản này danh sách ấy được CHÉP TAY ở BA nơi, cả ba đều `{pending, overdue}`:
 *   1. `report-payment.service.ts` — `REPORTABLE_STATUSES` (cổng GHI)
 *   2. `collection-detail.service.ts` — `REPORTABLE_STATUSES` (tính `canReportTransfer` cho UI)
 *   3. `report-payment.service.ts` — **mảng inline trong Prisma `where`** (danh sách đợt tự khai được)
 * Ba bản chép của một luật là đúng thứ `CLAUDE.md §3.8` gọi là *"đã có tiền lệ drift 2 whitelist"*.
 * Nay chỉ còn một chỗ để sửa, và sửa sai thì sai ĐỒNG BỘ chứ không lệch âm thầm giữa UI và cổng ghi.
 *
 * ═══ VÌ SAO THÊM `PARTIALLY_PAID` ═══
 * Bug người dùng báo 01/09/2026: nghĩa vụ 500, thủ quỹ đã ghi 200 ⇒ due thành `partially_paid` ⇒
 * member KHÔNG khai nốt 300 còn lại được, màn hình lại báo *"Quỹ này không cho tự báo chuyển khoản"*
 * — một câu nói về CẤU HÌNH NHÓM, trong khi nguyên nhân thật là TRẠNG THÁI KHOẢN. Đóng một phần rồi
 * thì phần còn thiếu vẫn là nợ, và người nợ vẫn phải trả được.
 *
 * 🔴 `AWAITING_CONFIRMATION` CỐ Ý KHÔNG có mặt: đã khai rồi thì chờ người duyệt, không khai chồng.
 * Muốn đổi số đã khai ⇒ **huỷ lời khai** (`canCancelReport`) rồi khai lại — QĐ người dùng 01/09/2026,
 * chọn có cân nhắc để một khoản luôn có ĐÚNG MỘT lời khai đang chờ, người duyệt không phải cộng nhẩm.
 * `PAID`/`PAID_ON_BEHALF`/`WAIVED` cũng không: không còn gì để trả.
 */
/**
 * 🔴🔴 NGUỒN SỰ THẬT DUY NHẤT — khoản CÒN PHẢI ĐÓNG (còn nợ tiền nhóm).
 *
 * ═══ VÌ SAO TÁCH KHỎI `REPORTABLE_MEMBER_DUE_STATUSES` DÙ HÔM NAY HAI TẬP TRÙNG NHAU ═══
 * Chúng trả lời HAI câu khác hẳn:
 *   · `REPORTABLE_*` — *"tôi còn TỰ KHAI chuyển khoản được không?"* (và còn phụ thuộc `mode`)
 *   · `OPEN_*`       — *"khoản này còn nợ không?"* (dùng để NHẮC và để xếp vào việc-của-tôi)
 *
 * Một khoản ở quỹ `admin_one_party` KHÔNG khai được nhưng VẪN còn nợ — hai câu đã lệch nhau ở đó,
 * chỉ là lệch ở tầng `mode` chứ không ở tầng status. Và nếu mai ta cho khai chồng khi đang chờ
 * duyệt thì `REPORTABLE_*` nhận thêm `awaiting_confirmation`, còn `OPEN_*` thì KHÔNG được — người
 * đã khai không phải người cần bị nhắc.
 * Gộp một tên cho hai câu là đúng "bẫy đặt tên nguy hiểm nhất" §7 đã ghi (`status` vs
 * `collectionStatus`): hai nghĩa dùng chung một tên thì ngày chúng tách nhau, không ai thấy.
 *
 * 🔴 `awaiting_confirmation` KHÔNG có mặt: người đã bấm "tôi đã chuyển" đã làm phần của họ. Nhắc họ
 * là đòi tiền một người đang chờ mình đối chiếu — `paid`/`paid_on_behalf`/`waived` cũng không.
 * `partially_paid` CÓ: phần còn thiếu vẫn là nợ, và trước 02/09/2026 nó bị bỏ sót ở CẢ HAI kênh
 * (danh sách việc + nhắc hạn) nên người đóng dở biến mất khỏi hệ thống.
 */
export const OPEN_MEMBER_DUE_STATUSES = [
  MEMBER_DUE_STATUS.PENDING,
  MEMBER_DUE_STATUS.OVERDUE,
  MEMBER_DUE_STATUS.PARTIALLY_PAID,
] as const satisfies readonly MemberDueStatus[];

export const REPORTABLE_MEMBER_DUE_STATUSES = [
  MEMBER_DUE_STATUS.PENDING,
  MEMBER_DUE_STATUS.OVERDUE,
  MEMBER_DUE_STATUS.PARTIALLY_PAID,
] as const satisfies readonly MemberDueStatus[];

/**
 * Ngưỡng "sắp đến hạn" (AC3): dueDate ≤ N ngày tính từ now (tz Asia/Ho_Chi_Minh) AND status=pending.
 * MẶC ĐỊNH 3 ngày (PO §6 — nhất quán toàn cụm E6). Đặt tên tránh magic number; server import.
 */
export const NEAR_DEADLINE_DAYS = 3;

/** Giá trị duy nhất hợp lệ cho query `member_id` — self/me (KHÔNG cho xem member khác, chống IDOR). */
export const MEMBER_ID_SELF = "me";

/** Regex 'YYYY-MM-DD' (date-only) — dueDate trả về. */
const PERIOD_ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const PERIOD_INTEGER_STRING_PATTERN = /^\d+$/;

/**
 * 1 đợt thu của tôi (AC1). status TỪ member_dues.status; isOverdue/isNearDeadline server-derive.
 *  - collectionId/title/currency/dueDate mô tả đợt; amountMinor là số tiền của member_due (STRING B1).
 *  - dueDate có thể null (đợt không đặt hạn).
 */
export const myCollectionPeriodItemSchema = z
  .object({
    collectionId: z.string().uuid(),
    title: z.string(),
    amountMinor: z.string().regex(PERIOD_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    dueDate: z.string().regex(PERIOD_ISO_DATE_PATTERN).nullable(),
    status: memberDueStatusSchema,
    /**
     * 🔴 US-E24.F2-0351 H3 (QĐ PO 07/08 · @UC9568) — trạng thái VÒNG ĐỜI CỦA ĐỢT (`open | closed`),
     * nguồn cho chip "Đã đóng". Tên KHÁC `status` là CỐ Ý (mirror `collectionDetail`): `status` ở
     * đây là member_due CỦA CALLER. Trước H3 danh sách ẨN HẲN đợt `closed` trong khi tổng vẫn cộng
     * dues của nó ⇒ thành viên bị đòi tiền cho một đợt KHÔNG NHÌN THẤY ĐƯỢC. Nay đợt vẫn hiện và
     * mang theo trạng thái của chính nó.
     */
    collectionStatus: collectionStatusSchema,
    isOverdue: z.boolean(),
    isNearDeadline: z.boolean(),
  })
  .strict();

export type MyCollectionPeriodItem = z.infer<typeof myCollectionPeriodItemSchema>;

/**
 * Envelope { periods: [...] } (getJson client nhận Map — KHÔNG array trần). Empty → { periods: [] }.
 *
 * 🔴🔴 ĐỌC KỸ TÊN ENDPOINT — `GET /v1/funds/{id}/collections` **KHÔNG phải danh sách cấp quỹ**.
 * Đây là **member-self**: chỉ trả các đợt thu ĐANG MỞ mà **CHÍNH caller** có `member_due`
 * (`view-collection-periods.service.ts:47-49` lọc `groupMemberId`; invariant `:24` *"IDOR: chỉ trả
 * due của caller"*). Caller không có due nào ⇒ `{ periods: [] }`, **không** rơi về danh sách của quỹ.
 * Vai OWNER/ADMIN/TREASURER **cũng chỉ thấy đợt của chính họ** — vai cao không nới phạm vi endpoint này.
 * `member_id` chỉ chấp nhận `'me'`/self; giá trị khác bị **BỎ QUA** (không cho xem member khác).
 *
 * ⚠️ `docs/SCREEN_API_MAP.md:110` ghi *"bản cũ map nhầm nghĩa"* — **đã có người hiểu sai một lần
 * rồi**. Biến endpoint này thành "danh sách của quỹ" = **mở due của người khác cho mọi member**,
 * đúng thứ `collection-period-status` (aggregate-only) cố ý chặn.
 *
 * ⇒ Cần **tiến độ đợt thu ở cấp QUỸ** (màn C1: mọi đợt của quỹ + "đã đóng X/Y" + "đã thu"): dùng
 * `GET /v1/funds/{id}/collection-progress` (`collection-progress.ts`, @UC9623) — endpoint RIÊNG, có
 * redaction tiền theo vai. **KHÔNG sửa endpoint này để lấy dữ liệu cấp quỹ.**
 * (US-E24.F18-0367-S02 · @UC9625 — quyết định (B): chỉ ghi rõ ngữ nghĩa, không thêm endpoint thứ ba.)
 */
export const myCollectionPeriodsResponseSchema = z
  .object({
    periods: z.array(myCollectionPeriodItemSchema),
  })
  .strict();

export type MyCollectionPeriodsResponse = z.infer<typeof myCollectionPeriodsResponseSchema>;

/* ===========================================================================
 * GET /v1/collections/:id — View Detail & Payment QR
 * (US-E6.F3-0039 / @UC0057, Phase 7 Collections — @gate rbac,pii). READ-only.
 *
 * Cross-check index §3 / docs/USER_STORIES.md [đã xoá → docs/USECASES.md UC0038-0040/UC0057] :1584 (PO chốt): trả chi tiết đợt thu CỦA CALLER (tên, số tiền,
 * hạn đóng, thông tin ngân hàng quỹ — PII member-only) + member_due của caller + các CỜ HIỂN THỊ:
 *  - canReportTransfer = (payment_confirmation_mode === 'two_party') AND status ∈ {pending, overdue}.
 *    Mode admin_one_party → false + confirmationHint (mobile map sang "Thủ quỹ sẽ ghi nhận…"). AC5.
 *  - canCancelReport = status === awaiting_confirmation (AC6 — client render nút "Huỷ báo CK"; hành vi
 *    huỷ thật ⇐ story 0040).
 *  - status paid/paid_on_behalf → isConfirmed=true + confirmedAt (AC4 — client ẩn QR/nút).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: amountMinor trả dạng STRING số nguyên (KHÔNG float/number > 2^53).
 *  - C4 RBAC + C2 PII: bank info CHỈ trả cho member ACTIVE của quỹ (CollectionAccessGuard); non-member/
 *    collection-không-tồn-tại/non-UUID → 404 đồng nhất (repo 404-uniformity — chống IDOR).
 *  - Status từ nguồn: status = member_dues.status nguyên gốc (KHÔNG recompute). Cờ canReportTransfer/
 *    canCancelReport/isConfirmed là server-derive (KHÔNG ghi DB).
 *  - Read-only: KHÔNG ghi ledger / KHÔNG mutate member_dues / KHÔNG tạo PaymentConfirmation.
 * ========================================================================= */

/** Key gợi ý cho client map text khi config admin_one_party (KHÔNG nhúng text VN vào API — i18n ở mobile). */
export const COLLECTION_CONFIRMATION_HINT = {
  /** Mobile map → "Thủ quỹ sẽ ghi nhận khi nhận được tiền" (AC5). */
  ADMIN_RECORDS: "admin_records",
} as const;

export type CollectionConfirmationHint =
  (typeof COLLECTION_CONFIRMATION_HINT)[keyof typeof COLLECTION_CONFIRMATION_HINT];

export const collectionConfirmationHintSchema = z.enum([
  COLLECTION_CONFIRMATION_HINT.ADMIN_RECORDS,
]);

/**
 * Thông tin ngân hàng trả về màn chi tiết (PII member-only). accountNo numeric — Copy STK (AC2).
 *
 * 🔴 `bankBin` OPTIONAL từ 31/08/2026 (vá review H-3 của US-E5.F2-0598). Nó bắt buộc suốt thời gian
 * đường ghi DUY NHẤT là form flutter-web (có ô chọn ngân hàng theo danh mục Napas ⇒ luôn có BIN).
 * Màn Cài đặt mobile của 0598 cố ý **không** có ô đó (QĐ-7: nhập tự do), và body cấp nhóm `.strict()`
 * từ chối `bankBin` ⇒ nhóm nào cấu hình bằng điện thoại thì jsonb **không bao giờ** có BIN. Giữ nó
 * bắt buộc nghĩa là những nhóm đó nhận `bankInfo: null` và client hiện "quỹ chưa cấu hình tài khoản
 * nhận" **dù đã nhập đủ STK + tên chủ TK + tên NH + ảnh QR** — app nói sai về trạng thái cấu hình
 * tiền. BIN chỉ phục vụ deep-link sang app ngân hàng; thiếu nó thì mất tiện ích, không mất dữ liệu.
 */
const COLLECTION_DETAIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const COLLECTION_DETAIL_INTEGER_STRING_PATTERN = /^\d+$/;

export const collectionDetailBankInfoSchema = z
  .object({
    bankBin: z.string().optional(),
    accountNo: z.string(),
    accountName: z.string(),
  })
  .strict();

export type CollectionDetailBankInfo = z.infer<typeof collectionDetailBankInfoSchema>;

/**
 * Chi tiết đợt thu + member_due của caller + cờ hiển thị (AC1/AC4/AC5/AC6).
 *  - bankInfo có thể null nếu quỹ chưa cấu hình tài khoản (client điều hướng / ẩn QR).
 *  - confirmedAt: ISO timestamp khi member_due đã paid (AC4) — null nếu chưa.
 *  - confirmationHint: chỉ set khi config admin_one_party (AC5) — null các mode khác.
 */
/**
 * 🔴 MOB-SEAT-PICK-DUES (08/10/2026 · @UC0057) — query của `GET /v1/collections/:id`: `memberId` TUỲ CHỌN = suất (`GroupMember.id`) mà người gọi ĐẠI DIỆN
 * (chủ suất hoặc liên kết sống, không `viewer`). Vắng ⇒ suất chính chủ của người gọi (hành vi cũ). Suất không đại diện / đã rời / không tồn tại ⇒ 404 ĐỒNG NHẤT.
 * `.strict()` chặn tham số lạ — chỉ một khoá, không có cờ nào mở rộng quyền.
 */
export const collectionDetailQuerySchema = z
  .object({
    memberId: z.string().uuid().optional(),
  })
  .strict();

export type CollectionDetailQuery = z.infer<typeof collectionDetailQuerySchema>;

/**
 * US-E16.F1-1023 (@UC0464 · RECEIPT-SPLIT S4) — đợt tạo THEO HÓA ĐƠN: tổng hóa đơn + ảnh (nếu có). Ai xem được đợt thu thì xem được (QĐ người
 * dùng 22/10 — tổng in ngay trên ảnh, không cho biết ai đã đóng). Ảnh trả URL đọc lại ngắn hạn, KHÔNG trả KEY R2.
 */
export const collectionDetailBillSchema = z
  .object({
    totalMinor: z.string().regex(/^[1-9]\d*$/u),
    receiptUrl: z.string().url().nullable(),
    receiptUrlExpiresAt: z.string().datetime().nullable(),
  })
  .strict();

export type CollectionDetailBill = z.infer<typeof collectionDetailBillSchema>;

export const collectionDetailResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    title: z.string(),
    amountMinor: z.string().regex(COLLECTION_DETAIL_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    dueDate: z.string().regex(COLLECTION_DETAIL_DATE_PATTERN).nullable(),
    /**
     * 🔴 `status` ở đây là trạng thái **MEMBER_DUE CỦA CALLER** (đã đóng hay chưa) — KHÔNG phải
     * trạng thái đợt thu. Tên này có trước và không đổi được nữa (client đang đọc).
     */
    status: memberDueStatusSchema,
    /**
     * 🔴 US-E24.F2-0351-S03 (@UC9567) — trạng thái VÒNG ĐỜI CỦA ĐỢT (`open | closed`), nguồn cho chip
     * "Đã hoàn thành". Đặt tên KHÁC `status` là CỐ Ý: hai nghĩa khác nhau tuyệt đối không được dùng
     * chung một tên trong cùng một object — đó đúng loại nhầm lẫn CLAUDE.md §7 gọi là "bẫy đặt tên
     * nguy hiểm nhất". Một due `paid` trong một đợt `open` và một due `pending` trong đợt `closed`
     * là hai tình huống khác hẳn nhau; gộp tên là mời người đọc kết luận sai về tiền.
     */
    collectionStatus: z.enum([COLLECTION_STATUS.OPEN, COLLECTION_STATUS.CLOSED]),
    bankInfo: collectionDetailBankInfoSchema.nullable(),
    canReportTransfer: z.boolean(),
    canCancelReport: z.boolean(),
    isConfirmed: z.boolean(),
    confirmedAt: z.string().datetime().nullable(),
    confirmationHint: collectionConfirmationHintSchema.nullable(),
    /**
     * 🔴 CÒN THIẾU BAO NHIÊU (01/09/2026) — `outstandingMinorOf`, nguồn CHUNG với mọi bề mặt tiến độ.
     *
     * Vì sao SERVER phải trả chứ không để client trừ: `apps/mobile/CLAUDE.md §4` — *"client HIỂN
     * THỊ, server QUYẾT ĐỊNH; client KHÔNG tính tiền"*. Và phép trừ ở đây không hề tầm thường —
     * `awaiting_confirmation` vẫn còn thiếu NGUYÊN khoản (§3.7: đã khai ≠ đã nhận), `waived` thì
     * bằng 0 cả hai vế. Client tự trừ `amountMinor − paidAmountMinor` sẽ nói sai ở cả hai ca đó.
     *
     * Bất biến (đã khoá bằng test ở `member-contributions.outstanding.spec.ts`):
     * `paidAmountMinor + outstandingMinor = amountMinor` cho mọi status trừ `waived`.
     */
    outstandingMinor: z.string().regex(COLLECTION_DETAIL_INTEGER_STRING_PATTERN),
    /** Đã đóng luỹ kế — `collectedMinorOf`. Cặp với `outstandingMinor` ở trên. */
    paidAmountMinor: z.string().regex(COLLECTION_DETAIL_INTEGER_STRING_PATTERN),
    /**
     * 🔴 SỐ ĐANG CHỜ DUYỆT — lời khai của CHÍNH caller chưa được ai đối chiếu, `null` khi không có.
     *
     * Đây là trạng thái tiền thứ (2) của §3.7, và nó KHÔNG được cộng vào `paidAmountMinor`: gộp
     * lại là biến một lời khai thành một khoản đã nhận. Tách riêng để màn hình nói được đúng câu
     * *"bạn đã khai X, đang chờ thủ quỹ xác nhận"* — thay vì im lặng hiện một ô tiền mời khai lần
     * nữa cho khoản mình vừa khai xong.
     */
    pendingClaimMinor: z
      .string()
      .regex(COLLECTION_DETAIL_INTEGER_STRING_PATTERN)
      .nullable(),
    /** @UC0464 — đợt theo hóa đơn: tổng + ảnh; đợt thường ⇒ null. */
    bill: collectionDetailBillSchema.nullable(),
  })
  .strict();

export type CollectionDetailResponse = z.infer<typeof collectionDetailResponseSchema>;

/* ===========================================================================
 * POST /v1/member-dues/:id/confirmations — Report Payment + Upload Proof
 * (US-E6.F3-0040 / @UC0057, Phase 7 Collections — @gate money,pii).
 *
 * Cross-check index §3 / docs/USER_STORIES.md [đã xoá → docs/USECASES.md UC0038-0040/UC0057] :1603 (PO chốt): member báo CK (tap "Tôi đã CK") →
 * tạo PaymentConfirmation(status=submitted) + claimed_amount_minor = amount của due →
 * member_due → awaiting_confirmation. Admin nhận notification (defer push P8.5).
 *
 * KHÔNG POST LEDGER ở task này — post CONTRIBUTION xảy ra khi admin duyệt (task 0041).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: claimedAmountMinor trả dạng STRING số nguyên (KHÔNG float).
 *  - C4 RBAC: CHỈ owner của member_due báo được (resolver từ guard ctx — chống IDOR).
 *  - C5 `.strict()`: chặn over-post (KHÔNG nhận memberDueId/submittedBy/status từ body).
 *  - FAIL-CLOSED mode: admin_one_party → 403 (member KHÔNG báo khi mode 1-đầu). unknown mode
 *    → lưu awaiting_confirmation (KHÔNG auto-confirm mù).
 *  - PII proof: magic-byte sniff (KHÔNG tin content-type), size ≤10MB, R2 storage.
 *  - NO LEDGER: KHÔNG ghi ledger_transaction ở task này (assert trong test).
 * ========================================================================= */

/** Trạng thái PaymentConfirmation (khớp PaymentConfirmation.status — schema.prisma:515). */
export const PAYMENT_CONFIRMATION_STATUS = {
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

/**
 * 🔴 US-E6.F3-0912 (review PR #1286 H1) — lý do SERVER tự ghi khi lời khai bị TỰ HUỶ (`DELETE /member-dues/:id/confirmations`). Tự
 * huỷ lưu `status='rejected'` (không có trạng thái riêng) và người huỷ có thể là MỘT người đại diện khác của cùng suất (bố khai, mẹ
 * huỷ) ⇒ `reviewedBy <> submittedBy` không đủ phân biệt "tự huỷ" với "bị từ chối". Chuỗi này do server đặt (không phải chữ người gõ)
 * và dữ liệu cũ mang đúng nó ⇒ là dấu phân loại tin được. MỘT chỗ khai: đường huỷ ghi, feed đọc.
 */
export const PAYMENT_CLAIM_SELF_CANCEL_REASON = "Người dùng tự huỷ";

/**
 * 🔴 ĐÍCH của một lời khai — đúng một (DB CHECK `payment_confirmations_target_check`). Client rẽ nhánh theo NHÃN này,
 * KHÔNG đoán từ `memberDueId`/`collectionId` null.
 *
 * 🔴 Ở ĐÂY chứ không ở `general-fund-claim.ts` (US-MOB.F9-1047): nó là thuộc tính của bảng `payment_confirmations`, cùng
 * họ với `PAYMENT_CONFIRMATION_STATUS` ngay trên, và nay có người dùng THỨ HAI là `groups.ts` (`rejected[]` của lịch sử
 * đóng góp). `general-fund-claim.ts` import `groups.js` nên để hằng ở đó thì người dùng thứ hai tạo VÒNG import; chép
 * một bản thứ hai thì đẻ đúng "drift 2 whitelist" mà `CLAUDE.md` §3.8 cấm. `general-fund-claim.ts` re-export nguyên văn
 * ⇒ mọi đường import cũ không đổi một ký tự.
 */
export const PAYMENT_CONFIRMATION_TARGET = {
  DUE: "due",
  GENERAL_FUND: "general_fund",
} as const;
export const paymentConfirmationTargetSchema = z.enum([
  PAYMENT_CONFIRMATION_TARGET.DUE,
  PAYMENT_CONFIRMATION_TARGET.GENERAL_FUND,
]);
export type PaymentConfirmationTarget = z.infer<typeof paymentConfirmationTargetSchema>;

export type PaymentConfirmationStatus =
  (typeof PAYMENT_CONFIRMATION_STATUS)[keyof typeof PAYMENT_CONFIRMATION_STATUS];

export const paymentConfirmationStatusSchema = z.enum([
  PAYMENT_CONFIRMATION_STATUS.SUBMITTED,
  PAYMENT_CONFIRMATION_STATUS.APPROVED,
  PAYMENT_CONFIRMATION_STATUS.REJECTED,
]);

/** Giới hạn kích thước proof = 10MB (story: ≤10MB, 1 ảnh). */
export const PROOF_MAX_BYTES = 10 * 1024 * 1024;

/** Content-type ảnh được chấp nhận cho proof (jpeg/png/webp — reuse avatar pattern). */
export const ALLOWED_PROOF_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ProofContentType = (typeof ALLOWED_PROOF_CONTENT_TYPES)[number];

/** Thông điệp lỗi user-facing — một nguồn sự thật api ↔ mobile (khớp AC). */
export const PROOF_TOO_LARGE_MESSAGE = "Ảnh quá lớn. Vui lòng chọn ảnh dưới 10MB.";
export const PROOF_UPLOAD_FAILED_MESSAGE = "Upload thất bại. Thử lại hoặc bỏ qua ảnh.";
export const PROOF_INVALID_FORMAT_MESSAGE =
  "Định dạng ảnh không hỗ trợ. Chỉ chấp nhận JPEG, PNG hoặc WebP.";
export const REPORT_MODE_NOT_ALLOWED_MESSAGE =
  "Quỹ không cho phép thành viên tự báo chuyển khoản.";
export const REPORT_STATUS_NOT_ALLOWED_MESSAGE =
  "Không thể báo chuyển khoản cho khoản thu này.";

/** Regex số nguyên dạng string (cho amount BigInt). */
const REPORT_PAYMENT_INTEGER_STRING_PATTERN = /^\d+$/;

/** Trần BIGINT Postgres — cùng hằng với `record-paid` (`packages/contracts/src/payments.ts`). */
const REPORT_PAYMENT_PG_BIGINT_MAX = 9223372036854775807n;

/**
 * Body cho POST /v1/member-dues/:id/confirmations.
 * - proof: file ảnh (optional) — validate tại biên (magic-byte, ≤10MB).
 * - `.strict()` chặn over-post (KHÔNG nhận claimedAmountMinor/submittedBy/status từ body —
 *   submittedBy từ token, status=submitted server set). ⚠️ Câu cũ ở đây còn ghi *"amount kế thừa
 *   member_due"*: HẾT ĐÚNG từ 01/09/2026 — số của lời khai nay là số NGƯỜI KHAI gõ, gửi qua
 *   `paidAmountMinor` bên dưới. Cột `claimedAmountMinor` vẫn không nhận trực tiếp từ body.
 *
 * NOTE: body chỉ chứa metadata nếu cần (vd ghi chú); file upload qua multipart.
 */
export const reportPaymentRequestSchema = z
  .object({
    /**
     * 🔴 GHI CHÚ ĐỐI CHIẾU — TUỲ CHỌN.
     * Đây là CHỮ ("chuyển lúc 9h qua Vietcombank"), thứ thủ quỹ dùng để soát sao kê.
     */
    note: z.string().trim().max(PAY_REPORT_NOTE_MAX_LENGTH).optional(),
    /**
     * 🔴🔴 SỐ THỰC KHAI — CÓ TÁC DỤNG Ở MỌI NHÓM (QĐ người dùng 01/09/2026).
     *
     * ⚠️ BẢN CŨ CỦA ĐOẠN NÀY LÀ NGUYÊN NHÂN CỦA MỘT BUG TIỀN, giữ lại nguyên văn để không ai khôi
     * phục: *"Field này KHÔNG đổi `claimedAmountMinor`… con số đó vẫn LUÔN kế thừa
     * `member_due.amountMinor`… Ở nhóm CHƯA bật cờ, server BỎ QUA field này"*.
     * Trong khi mobile VẪN cho sửa ô tiền và VẪN gửi số đó lên — nên member sửa 500 → 100, bấm
     * gửi, và sổ ghi 500. Màn hình mời người ta gõ một con số rồi vứt đi mà không nói gì.
     *
     * Nghĩa HIỆN TẠI, thống nhất ở mọi nhóm: **số người khai nói mình đã chuyển**. Nó ĐI THẲNG vào
     * `claimedAmountMinor`, và đó là con số thủ quỹ đọc lúc duyệt lẫn con số được post.
     *
     * Vắng ⇒ khai đúng phần CÒN THIẾU (không phải cả nghĩa vụ, nếu khoản đã đóng dở).
     * Partial/overpay dùng CÙNG công thức `record-paid` (`splitPartialPayment`), KHÔNG cơ chế thứ
     * hai (§3.8): < còn-nợ ⇒ `partially_paid`; = còn-nợ ⇒ `paid`; > còn-nợ ⇒ `paid` + dư →
     * `advance_credit` (theo `overpay_credit_policy`).
     *
     * 🔴 §3.7 KHÔNG bị nới: lời khai vẫn là trạng thái (2). Nhóm chưa bật
     * `self_payment_auto_confirm` thì nó vẫn dừng ở `submitted` chờ người đối chiếu — cái đổi là
     * CON SỐ của lời khai, không phải việc nó có được tính là đã nhận hay chưa.
     */
    paidAmountMinor: z
      .string()
      .trim()
      .regex(REPORT_PAYMENT_INTEGER_STRING_PATTERN, "Số tiền thực đóng phải là số nguyên (đơn vị nhỏ nhất)")
      .refine((v) => {
        try {
          const n = BigInt(v);
          return n > 0n && n <= REPORT_PAYMENT_PG_BIGINT_MAX;
        } catch {
          return false;
        }
      }, "Số tiền thực đóng phải lớn hơn 0 và không vượt giới hạn")
      .optional(),
    /**
     * 🔴🔴 KHOÁ NGHIỆP VỤ chống DOUBLE-POST (vá review PR #679 vòng 2) — MIRROR `record-paid`.
     *
     * Vì sao ở BODY chứ không phải header `Idempotency-Key`: bản vá đầu của tôi dùng header và viện
     * dẫn *"record-paid đã chạy đúng khuôn này từ US-E19.F1-0200"* — **sai ngược**. Chính 0200 đã
     * GỠ khuôn đó, và `record-paid.service.ts:97` ghi thẳng lý do: *"KHÔNG dùng `idempotencyKey`
     * (header client, có thể random mỗi lần) làm khoá ledger nữa"*. Header là thứ transport, ai
     * cũng có thể sinh ngẫu nhiên mỗi retry ⇒ unique index của ledger thành vô dụng.
     *
     * Field này khai TƯỜNG MINH là khoá NGHIỆP VỤ: hợp đồng của nó là *ổn định theo LẦN BẤM NÚT*,
     * không phải theo request. Client sinh một lần khi mở màn (`IdempotencySession`), gửi lại thì
     * gửi lại đúng chuỗi đó.
     *
     * VẮNG ⇒ server TỰ DERIVE từ nội dung (đường cũ) ⇒ client chưa nối VẪN được chống double-post.
     * Nhưng khi vắng, hai lượt đóng BẰNG NHAU trên cùng khoản sẽ va khoá ⇒ 409 — đó chính là lý do
     * field này tồn tại.
     */
    businessIdempotencyKey: businessIdempotencyKeySchema.optional(),
    /**
     * 🔴 US-E6.F3-0910 (@UC0057) — khoá file bằng chứng đã upload hai bước (`POST /funds/:id/contribution-evidence`).
     * Thay cho multipart `proof` (đường cũ vẫn giữ); gửi cả hai ⇒ 400. KHÔNG đi vào khoá idempotency (UUID ngẫu nhiên).
     */
    evidenceKey: evidenceKeySchema.optional(),
  })
  .strict();

export type ReportPaymentRequest = z.infer<typeof reportPaymentRequestSchema>;

/**
 * Shape response cho POST /v1/member-dues/:id/confirmations (201).
 *  - confirmationId: PaymentConfirmation vừa tạo.
 *  - memberDueId: liên kết MemberDue.
 *  - claimedAmountMinor: STRING BigInt (B1).
 *  - status: "submitted" (always).
 *  - proofUrl: R2 URL hoặc null (báo không ảnh).
 *  - newDueStatus: "awaiting_confirmation" (sau khi báo thành công).
 */
export const reportPaymentResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    claimedAmountMinor: z.string().regex(REPORT_PAYMENT_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /**
     * 🔴 US-E6.F3-0582 (@UC0057) — NỚI TỪ `z.literal(SUBMITTED)` SANG HAI GIÁ TRỊ.
     *
     * Cờ `self_payment_auto_confirm` BẬT ⇒ lời khai được duyệt NGAY trong cùng tx ⇒ hàng trong DB là
     * `approved`. Giữ `z.literal(SUBMITTED)` thì server phải trả một giá trị **không khớp DB** để lọt
     * schema — tức hợp đồng ép chính nó nói dối. Nới có chủ đích, đúng hai giá trị đạt được, và
     * `.strict()` bên dưới vẫn chặn mọi trạng thái khác.
     */
    status: z.enum([
      PAYMENT_CONFIRMATION_STATUS.SUBMITTED,
      PAYMENT_CONFIRMATION_STATUS.APPROVED,
    ]),
    proofUrl: z.string().url().nullable(),
    /**
     * 🔴 US-E6.F3-0582 (@UC0057) — cờ BẬT ⇒ due đã `paid`/`partially_paid` khi response rời server.
     * Client đọc field này để quyết định hiện "đang chờ đối chiếu" hay "đã đóng"; trả sai một giá
     * trị ở đây là hiện sai trạng thái TIỀN cho đúng người vừa nộp.
     *
     * 🔴🔴 US-E6.F3-0582-S02 — NỚI THÊM `partially_paid`. Member tự khai `paidAmountMinor` <
     * còn-nợ ở nhóm auto-confirm ⇒ due dừng ở đóng-một-phần, CÙNG ngữ nghĩa với `record-paid`
     * (`recordPaidResponseSchema.newDueStatus`) — không phải trạng thái mới, chỉ là contract này
     * trước đó chưa từng đạt tới nó.
     */
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.AWAITING_CONFIRMATION,
      MEMBER_DUE_STATUS.PARTIALLY_PAID,
      MEMBER_DUE_STATUS.PAID,
    ]),
    /**
     * 🔴🔴 US-E6.F3-0582-S02 — Σ đã đóng TÍCH LUỸ cho due này (không chỉ lần vừa rồi), STRING BigInt.
     * `null` khi lời khai CHƯA vào sổ (`newDueStatus=awaiting_confirmation`) — chưa có gì để cộng
     * dồn. Cùng field `recordPaidResponseSchema.paidAmountMinor`, khác `claimedAmountMinor` ở trên
     * (đó là số NGHĨA VỤ member khai, field này là số THỰC ĐÃ VÀO SỔ).
     */
    paidAmountMinor: z.string().regex(REPORT_PAYMENT_INTEGER_STRING_PATTERN).nullable(),
    /**
     * 🔴🔴 US-E6.F3-0582-S02 — còn nợ SAU lần đóng này, STRING BigInt. `null` cùng điều kiện với
     * `paidAmountMinor` ở trên. `'0'` khi đã đóng đủ/dư — KHÔNG bao giờ âm (§3.5, đồng lẻ có tên
     * người gánh không áp cho field này, nhưng nguyên tắc "không số âm" thì có).
     */
    remainingMinor: z.string().regex(REPORT_PAYMENT_INTEGER_STRING_PATTERN).nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type ReportPaymentResponse = z.infer<typeof reportPaymentResponseSchema>;

/* ===========================================================================
 * DELETE /v1/member-dues/:id/confirmations — Cancel Report (huỷ báo CK)
 * (US-E6.F3-0040 AC6 / @UC0057, Phase 7 Collections).
 *
 * Khi status = awaiting_confirmation (chưa admin duyệt) → huỷ báo CK → member_due về pending.
 * PaymentConfirmation KHÔNG xoá (audit trail) — chỉ cập nhật status → rejected (self-cancel).
 * ========================================================================= */

export const cancelReportResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    /**
     * 🔴 NỚI TỪ `z.literal(PENDING)` (QĐ người dùng 01/09/2026). Huỷ một lời khai trên khoản ĐÃ
     * ĐÓNG DỞ phải trả khoản đó về `partially_paid`, không phải `pending` — nếu không, phần đã vào
     * sổ cái biến mất khỏi mọi bề mặt tiến độ. Union hẹp: huỷ không đẻ ra được kết cục nào khác.
     */
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.PENDING,
      MEMBER_DUE_STATUS.PARTIALLY_PAID,
    ]),
    cancelledConfirmationId: z.string().uuid(),
  })
  .strict();

export type CancelReportResponse = z.infer<typeof cancelReportResponseSchema>;

/**
 * 🔴 PAY-ON-BEHALF-409-CODES (@UC0057) — MÃ ỔN ĐỊNH cho 409 của `DELETE /v1/member-dues/:id/confirmations` khi khoản KHÔNG còn ở
 * `awaiting_confirmation` (lời khai đã được xác nhận / từ chối, đã tự huỷ ở thiết bị khác, hoặc khoản đã được miễn). Trước đây service ném
 * 409 chuỗi trần ⇒ `code: "CONFLICT"` chung. `params.status` = trạng thái HIỆN TẠI của KHOẢN (đọc dưới khoá dòng khi có,
 * `MEMBER_DUE_STATUS`) — khác `PAYMENT_CONFIRMATION_NOT_PENDING` (trạng thái của LỜI KHAI), nên là mã riêng. Dòng khoản biến mất ⇒ 404.
 */
export const PAYMENT_REPORT_NOT_CANCELLABLE_CODE = "PAYMENT_REPORT_NOT_CANCELLABLE" as const;
